
import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { format } from 'date-fns';
import { 
  CalendarIcon, 
  UserPlus, 
  Check, 
  Loader2, 
  FileText, 
  Users, 
  CreditCard,
  RefreshCw
} from 'lucide-react';
import { Layout } from '@/components/layout/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/components/ui/use-toast';
import { useAdminAnalytics } from '@/hooks/useAdminAnalytics';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, BarChart, Bar } from 'recharts';
import { cn } from '@/lib/utils';
import { supabase, isUserAdmin } from '@/integrations/supabase/client';
import { startOfDay, endOfDay, subDays } from 'date-fns';

interface DatePreset {
  label: string;
  getDates: () => { startDate: Date; endDate: Date };
}

const AdminDashboardPage: React.FC = () => {
  const { analytics, dateRange, setDateRange, refreshAnalytics, isAdmin } = useAdminAnalytics();
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [adminCheckComplete, setAdminCheckComplete] = useState(false);
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [newAdminUsername, setNewAdminUsername] = useState('');
  const [isAddingAdmin, setIsAddingAdmin] = useState(false);
  const [admins, setAdmins] = useState<any[]>([]);
  const [isLoadingAdmins, setIsLoadingAdmins] = useState(false);
  const [activeTab, setActiveTab] = useState<string>('analytics');
  const { toast } = useToast();
  
  useEffect(() => {
    const checkAdminStatus = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.user?.id) {
          const adminResult = await isUserAdmin(session.user.id);
          console.log('Admin check in AdminDashboardPage:', { adminResult });
          setIsAuthorized(adminResult);
        } else {
          setIsAuthorized(false);
        }
      } catch (error) {
        console.error('Error checking admin status in dashboard:', error);
        setIsAuthorized(false);
      } finally {
        setAdminCheckComplete(true);
      }
    };
    
    checkAdminStatus();
  }, []);
  
  useEffect(() => {
    if (isAuthorized) {
      fetchAdmins();
    }
  }, [isAuthorized]);
  
  const fetchAdmins = async () => {
    setIsLoadingAdmins(true);
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('id, username, name, email')
        .eq('role', 'admin')
        .order('username', { ascending: true });
      
      if (error) throw error;
      setAdmins(data || []);
    } catch (error) {
      console.error('Error fetching admins:', error);
      toast({
        title: 'Error',
        description: 'Failed to load admin users',
        variant: 'destructive',
      });
    } finally {
      setIsLoadingAdmins(false);
    }
  };
  
  const handleAddAdmin = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!newAdminUsername.trim()) {
      toast({
        title: 'Error',
        description: 'Please enter a username',
        variant: 'destructive',
      });
      return;
    }
    
    setIsAddingAdmin(true);
    
    try {
      // Find user by username
      const { data: userData, error: userError } = await supabase
        .from('profiles')
        .select('id, username, role')
        .eq('username', newAdminUsername.trim())
        .maybeSingle();
      
      if (userError && userError.code !== 'PGRST116') throw userError;
      
      if (!userData) {
        toast({
          title: 'User not found',
          description: `No user with username '${newAdminUsername}' was found`,
          variant: 'destructive',
        });
        setIsAddingAdmin(false);
        return;
      }
      
      if (userData.role === 'admin') {
        toast({
          title: 'Already an admin',
          description: `${newAdminUsername} is already an admin`,
          variant: 'destructive',
        });
        setIsAddingAdmin(false);
        return;
      }
      
      // Update the user's role to admin
      const { error: updateError } = await supabase
        .from('profiles')
        .update({ role: 'admin' })
        .eq('id', userData.id);
      
      if (updateError) throw updateError;
      
      toast({
        title: 'Success',
        description: `${newAdminUsername} is now an admin`,
        className: 'bg-green-500 text-white',
      });
      
      setNewAdminUsername('');
      fetchAdmins();
    } catch (error) {
      console.error('Error adding admin:', error);
      toast({
        title: 'Error',
        description: 'Failed to add admin user',
        variant: 'destructive',
      });
    } finally {
      setIsAddingAdmin(false);
    }
  };
  
  if (!adminCheckComplete) {
    return (
      <Layout>
        <div className="container mx-auto px-4 py-8">
          <div className="flex items-center justify-center h-96">
            <Skeleton className="h-12 w-12 rounded-full" />
          </div>
        </div>
      </Layout>
    );
  }
  
  if (!isAuthorized) {
    return <Navigate to="/app" replace />;
  }
  
  const { totalUsers, totalGroups, totalExpenses, dailyStats, isLoading, error } = analytics;
  
  const formattedStartDate = format(dateRange.startDate, 'MMM d, yyyy');
  const formattedEndDate = format(dateRange.endDate, 'MMM d, yyyy');
  
  // Define preset date ranges
  const datePresets: DatePreset[] = [
    {
      label: 'Today',
      getDates: () => {
        const today = new Date();
        return {
          startDate: startOfDay(today),
          endDate: endOfDay(today)
        };
      }
    },
    {
      label: 'Last 7 Days',
      getDates: () => {
        const end = new Date();
        const start = subDays(end, 6); // 6 days back + today = 7 days
        return { 
          startDate: startOfDay(start),
          endDate: endOfDay(end) 
        };
      }
    },
    {
      label: 'Last 28 Days',
      getDates: () => {
        const end = new Date();
        const start = subDays(end, 27); // 27 days back + today = 28 days
        return { 
          startDate: startOfDay(start),
          endDate: endOfDay(end) 
        };
      }
    },
    {
      label: 'Maximum',
      getDates: () => ({
        startDate: new Date(2021, 0, 1), // Start from January 1, 2021
        endDate: endOfDay(new Date())
      })
    }
  ];

  // Handle preset date selection
  const handlePresetSelect = (preset: DatePreset) => {
    const newRange = preset.getDates();
    console.log(`Selected preset: ${preset.label}`, newRange);
    setDateRange(newRange);
  };

  // Handle date selection in the calendar
  const handleDateSelect = (range: { from?: Date; to?: Date }) => {
    if (range.from) {
      const newRange = {
        startDate: startOfDay(range.from),
        endDate: endOfDay(range.to || range.from)
      };
      console.log('Selected date range:', newRange);
      setDateRange(newRange);
      
      if (range.to) {
        setIsCalendarOpen(false);
      }
    }
  };

  const calculateDailyAverages = () => {
    if (dailyStats.length === 0) return { users: 0, groups: 0, expenses: 0 };
    
    const totalDays = dailyStats.length;
    const totalDailyUsers = dailyStats.reduce((sum, day) => sum + day.total_users, 0);
    const totalDailyGroups = dailyStats.reduce((sum, day) => sum + day.total_groups, 0);
    const totalDailyExpenses = dailyStats.reduce((sum, day) => sum + day.total_expenses, 0);
    
    return {
      users: totalDailyUsers / totalDays,
      groups: totalDailyGroups / totalDays,
      expenses: totalDailyExpenses / totalDays
    };
  };
  
  const dailyAverages = calculateDailyAverages();
  
  const processChartData = () => {
    // Create accumulating totals for cumulative view
    let cumulativeUsers = 0;
    let cumulativeGroups = 0;
    let cumulativeExpenses = 0;
    
    return [...dailyStats].map(day => {
      // Add daily values to cumulative totals
      cumulativeUsers += day.total_users;
      cumulativeGroups += day.total_groups;
      cumulativeExpenses += day.total_expenses;
      
      return {
        date: day.date,
        formattedDate: format(new Date(day.date), 'MMM d'),
        users: day.total_users,
        groups: day.total_groups,
        expenses: day.total_expenses,
        cumulativeUsers,
        cumulativeGroups,
        cumulativeExpenses
      };
    });
  };
  
  const chartData = processChartData();
  
  return (
    <Layout>
      <div className="container mx-auto px-4 py-4 pb-20">
        <div className="flex flex-col space-y-4 md:flex-row md:justify-between md:items-center mb-6">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Admin Dashboard</h1>
            <p className="text-gray-600">Monitor metrics and manage users</p>
          </div>
        </div>
        
        <Tabs defaultValue="analytics" value={activeTab} onValueChange={setActiveTab} className="mb-6">
          <TabsList className="mb-4 w-full justify-start overflow-x-auto">
            <TabsTrigger value="analytics" className="flex items-center gap-2">
              <FileText className="h-4 w-4" />
              <span className="hidden sm:inline">Analytics</span>
            </TabsTrigger>
            <TabsTrigger value="admins" className="flex items-center gap-2">
              <Users className="h-4 w-4" />
              <span className="hidden sm:inline">Admin Users</span>
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="analytics" className="space-y-6">
            {/* Date selection toolbar */}
            <div className="bg-white p-4 rounded-lg shadow-sm border mb-4">
              <div className="flex flex-wrap gap-2 justify-between items-center">
                <div className="flex flex-wrap gap-2">
                  {datePresets.map((preset) => (
                    <Button
                      key={preset.label}
                      variant="outline"
                      size="sm"
                      className="text-xs sm:text-sm"
                      onClick={() => handlePresetSelect(preset)}
                    >
                      {preset.label}
                    </Button>
                  ))}
                </div>
                
                <div className="flex flex-wrap gap-2">
                  <Popover open={isCalendarOpen} onOpenChange={setIsCalendarOpen}>
                    <PopoverTrigger asChild>
                      <Button variant="outline" size="sm" className="min-w-[150px] sm:min-w-[200px]">
                        <CalendarIcon className="h-3 w-3 mr-1 sm:h-4 sm:w-4 sm:mr-2" />
                        <span className="text-xs sm:text-sm truncate">
                          {formattedStartDate} - {formattedEndDate}
                        </span>
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="end">
                      <Calendar
                        mode="range"
                        selected={{
                          from: dateRange.startDate,
                          to: dateRange.endDate
                        }}
                        onSelect={handleDateSelect}
                        initialFocus
                        numberOfMonths={window.innerWidth > 768 ? 2 : 1}
                        disabled={(date) => 
                          date > new Date() || date < new Date(2021, 0, 1)
                        }
                      />
                    </PopoverContent>
                  </Popover>
                  
                  <Button 
                    size="sm" 
                    onClick={refreshAnalytics} 
                    disabled={isLoading}
                    className="px-2 sm:px-4"
                  >
                    {isLoading ? (
                      <Loader2 className="h-3 w-3 sm:h-4 sm:w-4 animate-spin" />
                    ) : (
                      <RefreshCw className="h-3 w-3 sm:h-4 sm:w-4" />
                    )}
                    <span className="ml-1 sm:ml-2 text-xs sm:text-sm">Refresh</span>
                  </Button>
                </div>
              </div>
            </div>
            
            {error ? (
              <Card className="mb-6 bg-red-50 border-red-200">
                <CardContent className="pt-6">
                  <p className="text-red-600">{error}</p>
                  <Button className="mt-2" onClick={refreshAnalytics}>Try Again</Button>
                </CardContent>
              </Card>
            ) : null}
            
            {/* Stats cards */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
              <Card>
                <CardHeader className="pb-2 p-4">
                  <CardTitle className="text-sm flex items-center font-medium text-gray-500">
                    <Users className="mr-2 h-4 w-4 text-primary" />
                    Total Users
                  </CardTitle>
                  {isLoading ? (
                    <Skeleton className="h-8 w-1/2" />
                  ) : (
                    <div className="text-2xl font-bold">{totalUsers}</div>
                  )}
                  <CardDescription className="text-xs text-gray-500">
                    ~{dailyAverages.users.toFixed(1)}/day
                  </CardDescription>
                </CardHeader>
              </Card>
              
              <Card>
                <CardHeader className="pb-2 p-4">
                  <CardTitle className="text-sm flex items-center font-medium text-gray-500">
                    <FileText className="mr-2 h-4 w-4 text-pink-500" />
                    Total Groups
                  </CardTitle>
                  {isLoading ? (
                    <Skeleton className="h-8 w-1/2" />
                  ) : (
                    <div className="text-2xl font-bold">{totalGroups}</div>
                  )}
                  <CardDescription className="text-xs text-gray-500">
                    ~{dailyAverages.groups.toFixed(1)}/day
                  </CardDescription>
                </CardHeader>
              </Card>
              
              <Card>
                <CardHeader className="pb-2 p-4">
                  <CardTitle className="text-sm flex items-center font-medium text-gray-500">
                    <CreditCard className="mr-2 h-4 w-4 text-blue-500" />
                    Total Expenses
                  </CardTitle>
                  {isLoading ? (
                    <Skeleton className="h-8 w-1/2" />
                  ) : (
                    <div className="text-2xl font-bold">{totalExpenses}</div>
                  )}
                  <CardDescription className="text-xs text-gray-500">
                    ~{dailyAverages.expenses.toFixed(1)}/day
                  </CardDescription>
                </CardHeader>
              </Card>
            </div>
            
            {/* Charts section */}
            <Tabs defaultValue="daily" className="mb-6">
              <TabsList className="mb-4">
                <TabsTrigger value="daily">Daily Activity</TabsTrigger>
                <TabsTrigger value="cumulative">Cumulative Growth</TabsTrigger>
              </TabsList>
              
              <TabsContent value="daily" className="space-y-4">
                <Card>
                  <CardHeader className="p-4">
                    <CardTitle>Daily Activity</CardTitle>
                    <CardDescription>
                      New users, groups, and expenses created each day
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="p-0 sm:p-4">
                    {isLoading ? (
                      <Skeleton className="h-[300px] sm:h-[350px] w-full" />
                    ) : chartData.length === 0 ? (
                      <div className="h-[300px] sm:h-[350px] flex items-center justify-center text-gray-500">
                        No data available for the selected time period
                      </div>
                    ) : (
                      <div className="h-[300px] sm:h-[350px] w-full">
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={chartData} margin={{ top: 5, right: 5, bottom: 5, left: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis 
                              dataKey="formattedDate" 
                              tick={{ fontSize: 10 }}
                              interval="preserveStartEnd"
                            />
                            <YAxis tick={{ fontSize: 10 }} />
                            <Tooltip />
                            <Legend wrapperStyle={{ fontSize: '10px' }} />
                            <Bar dataKey="users" name="Users" fill="#5770fd" />
                            <Bar dataKey="groups" name="Groups" fill="#ff66c4" />
                            <Bar dataKey="expenses" name="Expenses" fill="#38bdf8" />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
              
              <TabsContent value="cumulative" className="space-y-4">
                <Card>
                  <CardHeader className="p-4">
                    <CardTitle>Cumulative Growth</CardTitle>
                    <CardDescription>
                      Total growth over the selected time period
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="p-0 sm:p-4">
                    {isLoading ? (
                      <Skeleton className="h-[300px] sm:h-[350px] w-full" />
                    ) : chartData.length === 0 ? (
                      <div className="h-[300px] sm:h-[350px] flex items-center justify-center text-gray-500">
                        No data available for the selected time period
                      </div>
                    ) : (
                      <div className="h-[300px] sm:h-[350px]">
                        <ResponsiveContainer width="100%" height="100%">
                          <LineChart data={chartData} margin={{ top: 5, right: 5, bottom: 5, left: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis 
                              dataKey="formattedDate" 
                              tick={{ fontSize: 10 }}
                              interval="preserveStartEnd"
                            />
                            <YAxis tick={{ fontSize: 10 }} />
                            <Tooltip />
                            <Legend wrapperStyle={{ fontSize: '10px' }} />
                            <Line 
                              type="monotone" 
                              dataKey="cumulativeUsers" 
                              name="Users" 
                              stroke="#5770fd" 
                              activeDot={{ r: 6 }}
                              strokeWidth={2}
                            />
                            <Line 
                              type="monotone" 
                              dataKey="cumulativeGroups" 
                              name="Groups" 
                              stroke="#ff66c4" 
                              strokeWidth={2}
                            />
                            <Line 
                              type="monotone" 
                              dataKey="cumulativeExpenses" 
                              name="Expenses" 
                              stroke="#38bdf8" 
                              strokeWidth={2}
                            />
                          </LineChart>
                        </ResponsiveContainer>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
            
            {/* Daily stats table */}
            <Card>
              <CardHeader className="p-4">
                <CardTitle>Daily Statistics</CardTitle>
                <CardDescription>Detailed daily breakdown of all metrics</CardDescription>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full text-xs sm:text-sm text-left">
                    <thead className="text-xs text-gray-700 uppercase bg-gray-50">
                      <tr>
                        <th className="px-3 py-2 sm:px-6 sm:py-3">Date</th>
                        <th className="px-3 py-2 sm:px-6 sm:py-3">New Users</th>
                        <th className="px-3 py-2 sm:px-6 sm:py-3">New Groups</th>
                        <th className="px-3 py-2 sm:px-6 sm:py-3">New Expenses</th>
                      </tr>
                    </thead>
                    <tbody>
                      {isLoading ? (
                        Array(5).fill(0).map((_, i) => (
                          <tr key={i} className="bg-white border-b">
                            <td className="px-3 py-2 sm:px-6 sm:py-4"><Skeleton className="h-4 w-16" /></td>
                            <td className="px-3 py-2 sm:px-6 sm:py-4"><Skeleton className="h-4 w-8" /></td>
                            <td className="px-3 py-2 sm:px-6 sm:py-4"><Skeleton className="h-4 w-8" /></td>
                            <td className="px-3 py-2 sm:px-6 sm:py-4"><Skeleton className="h-4 w-8" /></td>
                          </tr>
                        ))
                      ) : dailyStats.length === 0 ? (
                        <tr className="bg-white border-b">
                          <td colSpan={4} className="px-3 py-2 sm:px-6 sm:py-4 text-center text-gray-500">
                            No data available for the selected time period
                          </td>
                        </tr>
                      ) : (
                        dailyStats.map((day, index) => (
                          <tr key={index} className="bg-white border-b hover:bg-gray-50">
                            <td className="px-3 py-2 sm:px-6 sm:py-3">{format(new Date(day.date), 'MMM d, yyyy')}</td>
                            <td className="px-3 py-2 sm:px-6 sm:py-3">{day.total_users}</td>
                            <td className="px-3 py-2 sm:px-6 sm:py-3">{day.total_groups}</td>
                            <td className="px-3 py-2 sm:px-6 sm:py-3">{day.total_expenses}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="admins" className="space-y-6">
            {/* Admin Management section */}
            <Card>
              <CardHeader className="p-4">
                <CardTitle>Admin Management</CardTitle>
                <CardDescription>
                  Add or manage administrator users
                </CardDescription>
              </CardHeader>
              <CardContent className="p-4">
                <form onSubmit={handleAddAdmin} className="flex flex-col space-y-4 mb-6">
                  <div className="flex flex-col sm:flex-row sm:items-end gap-3">
                    <div className="flex-1">
                      <Label htmlFor="newAdminUsername">Add Admin by Username</Label>
                      <Input
                        id="newAdminUsername"
                        placeholder="Enter username"
                        value={newAdminUsername}
                        onChange={(e) => setNewAdminUsername(e.target.value)}
                        className="mt-1"
                      />
                    </div>
                    <Button 
                      type="submit" 
                      disabled={isAddingAdmin} 
                      className="sm:mb-0 flex items-center"
                    >
                      {isAddingAdmin ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Adding...
                        </>
                      ) : (
                        <>
                          <UserPlus className="mr-2 h-4 w-4" />
                          Add Admin
                        </>
                      )}
                    </Button>
                  </div>
                </form>
                
                <Separator className="my-4" />
                
                <h3 className="font-medium text-lg mb-3">Current Admins</h3>
                
                {isLoadingAdmins ? (
                  <div className="space-y-2">
                    <Skeleton className="h-12 w-full" />
                    <Skeleton className="h-12 w-full" />
                    <Skeleton className="h-12 w-full" />
                  </div>
                ) : admins.length === 0 ? (
                  <p className="text-gray-500">No admin users found</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs sm:text-sm text-left">
                      <thead className="text-xs text-gray-700 uppercase bg-gray-50">
                        <tr>
                          <th className="px-3 py-2 sm:px-6 sm:py-3">Username</th>
                          <th className="px-3 py-2 sm:px-6 sm:py-3">Name</th>
                          <th className="px-3 py-2 sm:px-6 sm:py-3">Email</th>
                        </tr>
                      </thead>
                      <tbody>
                        {admins.map((admin) => (
                          <tr key={admin.id} className="bg-white border-b hover:bg-gray-50">
                            <td className="px-3 py-2 sm:px-6 sm:py-3 font-medium">{admin.username}</td>
                            <td className="px-3 py-2 sm:px-6 sm:py-3">{admin.name || '-'}</td>
                            <td className="px-3 py-2 sm:px-6 sm:py-3">{admin.email || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
};

export default AdminDashboardPage;
